<template>
  <el-header class="navbar navbar-expand-lg navbar-dark bg-header bg-company">
    <div class="container-fluid d-flex align-items-center justify-content-between">
      <!-- Logo -->
      <a class="navbar-brand p-0 m-0" href="#">
        <img id="logo" :src="userStore.domain?.logo ?? defaultLogoPathWhite" alt="Home" @click="goToCompanyWebsite(userStore.domain?.companyWebsite ?? '')" />
      </a>
      <div>
        <div class="col d-flex justify-content-end gap-2 me-4">
          <p class="my-auto" v-if="userStore.isAuthenticated">{{ $t('components.custom-header.loggedInAs') }} {{ userStore.user?.name }}</p>
          <button v-if="userStore.isAuthenticated" type="button" class="btn logout-btn" @click="handleLogout()">
            {{ $t('components.custom-header.logout') }}
          </button>
          <button v-if="!userStore.isAuthenticated" type="button" class="btn login-btn" @click="goToLogin()">
            {{ $t('components.custom-header.login') }}
          </button>
          <el-dropdown trigger="click" @command="setLanguage">
            <el-button link class="dropdown-toggle">
              {{ locale.toUpperCase() }}
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item
                  v-for="lang in languageOptions"
                  :key="lang.key"
                  :command="lang.key"
                >
                  {{ lang.label }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>
  </el-header>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import Cookies from 'js-cookie';

import { WorkflowModel } from '@/types/ui/WorkflowModel';
import MeshEditor from '@/components/three/scenes/MeshEditor.vue';
import { removeWordFromString } from '@/utils/formBuilder';
import { Key } from '@/types/constants/Key';
import { getI18nLocale, setI18nLocale } from '@/i18n';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { languageOptions } from '@/types/constants/language';
import { defaultLogoPathWhite } from '@/utils/theme';

@Component
export default class Header extends Vue {
  @Prop({ default: false }) isEditor!: boolean;
  @Prop() workflowModel?: WorkflowModel<any>;
  @Prop({ default: '' }) productName!: string;
  @Prop({ default: 3 }) selectedProductQualityIndex!: number;
  @Prop({ default: null }) meshEditor!: InstanceType<typeof MeshEditor>;

  defaultLogoPathWhite = defaultLogoPathWhite
  locale = getI18nLocale();
  router = useRouter();
  route= useRoute();
  dropdownOpen = false;
  loading = false;
  userStore = useUserStore();
  Key = Key
  languageOptions = languageOptions

  created() {
    const storedLang = localStorage.getItem(Key.storage.locale);
    if (storedLang) {
      setI18nLocale(storedLang);
      this.locale = storedLang;
    } else {
      const browserLang = navigator.language;
      if (browserLang.toLowerCase().startsWith(Key.language.english)) {
        setI18nLocale(Key.language.english);
        this.locale = Key.language.english;
      } else {
        setI18nLocale(Key.language.default);
        this.locale = Key.language.default;
      }
    }
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  setLanguage(lang: string) {
    setI18nLocale(lang);
    localStorage.setItem(Key.storage.locale, lang);
    this.locale = getI18nLocale();
    this.dropdownOpen = false;
    window.location.reload();
  }

  productQualityChanged(selectedProductQuality: string) {
    this.$emit('productQualityChanged', selectedProductQuality);
  }

  // Truncate 'phase1' word in title string
  get productTitle(): string {
    return removeWordFromString(this.productName, 'phase1', 20);
  }

  goToLogin() {
    this.router.push({ path: '/login', query: { redirect: this.route.fullPath } });
  }

  handleLogout() {
    this.loading = true;
    Cookies.remove(Key.cookie.access_token_key);
    localStorage.clear();
    this.loading = false;
    window.location.reload();
  }

  goToCompanyWebsite(companyWebsite: string) {
    window.open(companyWebsite, '_blank');
  }
}
</script>

<style scoped lang="scss">
.editor-view {
  position: relative;
  z-index: 1;
}

.bg-header {
  background-color: var(--color-primary) !important;
  height: 7vh;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

#logo {
  max-height: 5.3vh;
  height: auto;
  width: auto;
  padding: 0.3rem;
  background-color: #ffffff;
  border-radius: 0.25rem;
}

.navbar-brand {
  display: flex;
  align-items: center;
}

.img-btn {
  padding: 0;
}

p {
  font-weight: 600;
  font-size: 1rem;
}

.btn {
  font-size: 0.9rem !important;
  border-radius: 0.25rem !important;
  font-weight: 700 !important;
  margin-left: 0.7rem;
}

.login-btn {
  background-color: #73f57e;

  &:hover {
    background-color: #73f57e !important;
    color: #ffffff;
  }
}

.logout-btn {
  background-color: #f5756c;

  &:hover {
    background-color: #f5756c !important;
    color: #ffffff;
  }
}

.dropdown-toggle {
  color: #ffffff !important;
  font-size: 1rem !important;
  font-weight: 700 !important;
  padding: 0.1rem 0.4rem;
  border-radius: 0.25rem !important;

  &:hover {
    background-color: #ffffff2e !important;
  }
}

:deep(.el-dropdown-menu__item) {
  font-size: 1rem !important;
}

@media (max-width: 768px) {
  .bg-header {
    flex-wrap: wrap;
    height: auto;
  }

  .navbar-brand img {
    max-height: 4vh;
  }

  .btn {
    font-size: 0.75rem;
  }
}
</style>
